import { useEffect } from 'react';
import { Controller } from 'react-hook-form';

import { ComponentSettingRadio } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import Markdown from 'dashboard/components/Markdown';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import { Label, Paragraph } from 'design-system/components/Text';
import { UiComponentConfig } from 'lib/interfaces/ui-components';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface RadioSettingProps {
  editable: boolean;
  componentSetting: ComponentSettingRadio;
  componentConfig: UiComponentConfig['config'];
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
}

const RadioSetting = ({
  editable,
  componentSetting,
  componentConfig,
  handleUpdateComponentConfig,
}: RadioSettingProps): JSX.Element => {
  const { 'client-key': clientKey, 'helper-text': helperText, options } = componentSetting;

  const initialValue = componentConfig?.[clientKey as keyof UiComponentConfig['config']];

  const { control, handleSubmit, reset } = useFormSecurely({
    defaultValues: {
      [clientKey]: initialValue,
    },
  });

  const handleSubmitForm = handleSubmit((data) => {
    handleUpdateComponentConfig({ [clientKey]: data[clientKey] });
  });

  useEffect(() => reset({ [clientKey]: initialValue }), [initialValue, reset, clientKey]);

  return (
    <>
      <Label weight='bold'>
        {componentSetting.label}
        {helperText && (
          <HelpIcon>
            <Paragraph size='sm'>
              <Markdown>{helperText}</Markdown>
            </Paragraph>
          </HelpIcon>
        )}
      </Label>
      <Controller
        name={clientKey}
        control={control}
        render={({ field: { value, onChange } }) => (
          <RadioGroup
            id={`form-section-radio-setting__${clientKey}`}
            value={value}
            onChange={(value) => {
              onChange(value);
              handleSubmitForm();
            }}
            horizontal={options.length <= 2}
          >
            {options.map(({ label, value }) => (
              <Radio key={value} label={label} value={value} disabled={!editable} />
            ))}
          </RadioGroup>
        )}
      />
    </>
  );
};

export default RadioSetting;
